import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Star, Search, X, GripVertical, Download, Filter, CheckSquare, Square } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VehiclesManagement = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteVehicle, setDeleteVehicle] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Photo background enhancement
  const [enhanceStatus, setEnhanceStatus] = useState(null); // {status, processed, total_vehicles, ...}
  const [enhancingVehicleId, setEnhancingVehicleId] = useState(null);
  
  // Bulk operations
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  
  // Advanced filters
  const [filters, setFilters] = useState({
    status: '',
    brand: '',
    priceMin: '',
    priceMax: '',
    yearMin: '',
    yearMax: '',
    duplicateVin: false  // Filter for vehicles with duplicate VINs
  });
  
  // Filter panel visibility
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Status tab - all, available, sold
  const [activeTab, setActiveTab] = useState('available');
  
  const [vehicleForm, setVehicleForm] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    color: '',
    transmission: 'Automatic',
    fuel_type: 'Gasoline',
    engine: '',
    vin: '',
    description: '',
    features: '',
    images: '',
    is_featured: false
  });


  // Bulk operations handlers
  const handleSelectAll = () => {
    if (selectedVehicles.length === filteredVehicles.length) {
      setSelectedVehicles([]);
    } else {
      setSelectedVehicles(filteredVehicles.map(v => v.id));
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedVehicles.length} vehicles? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/vehicles/bulk-delete`, 
        { vehicle_ids: selectedVehicles },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(`${selectedVehicles.length} vehicles deleted`);
      setSelectedVehicles([]);
      fetchVehicles();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete vehicles');
    }
  };

  const handleBulkUpdateStatus = async (status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/vehicles/bulk-update-status`, 
        { vehicle_ids: selectedVehicles, status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(`${selectedVehicles.length} vehicles updated to ${status}`);
      setSelectedVehicles([]);
      setBulkAction('');
      fetchVehicles();
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Failed to update vehicles');
    }
  };

  const handleBulkToggleFeatured = async (isFeatured) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/vehicles/bulk-toggle-featured`, 
        { vehicle_ids: selectedVehicles, is_featured: isFeatured },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(`${selectedVehicles.length} vehicles ${isFeatured ? 'featured' : 'unfeatured'}`);
      setSelectedVehicles([]);
      setBulkAction('');
      fetchVehicles();
    } catch (error) {
      console.error('Bulk feature error:', error);
      toast.error('Failed to update vehicles');
    }
  };

  // Excel Export Handler
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/vehicles/admin/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vehicles_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('admin.success') + '!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('admin.error'));
    } finally {
      setExporting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      brand: '',
      priceMin: '',
      priceMax: '',
      yearMin: '',
      yearMax: '',
      duplicateVin: false
    });
    setSearchQuery('');
    setVinSearch('');
  };

  // Get unique brands for filter dropdown - exclude years (4-digit numbers)
  const uniqueBrands = [...new Set(vehicles.map(v => v.brand).filter(brand => {
    if (!brand) return false;
    // Exclude if brand is just a year (4 digits)
    if (/^\d{4}$/.test(brand.trim())) return false;
    // Exclude if brand starts with a number
    if (/^\d/.test(brand.trim())) return false;
    return true;
  }))].sort();

  // Get duplicate VINs (VINs that appear more than once in the SAME status/tab)
  const currentTabVehicles = activeTab === 'all' 
    ? vehicles 
    : vehicles.filter(v => v.status === activeTab);
  
  const vinCounts = currentTabVehicles.reduce((acc, v) => {
    if (v.vin && v.vin.trim()) {  // Only count non-empty VINs
      acc[v.vin] = (acc[v.vin] || 0) + 1;
    }
    return acc;
  }, {});
  const duplicateVins = new Set(Object.keys(vinCounts).filter(vin => vinCounts[vin] > 1));

  // VIN search state
  const [vinSearch, setVinSearch] = useState('');

  // Filtered vehicles based on tab and advanced filters
  const filteredVehicles = vehicles.filter(vehicle => {
    // First filter by active tab (ALWAYS apply tab filter)
    if (activeTab !== 'all' && vehicle.status !== activeTab) return false;
    
    // VIN search - if active, only match by VIN (but still respect tab filter above)
    if (vinSearch) {
      const vin = vehicle.vin?.toLowerCase() || '';
      return vin.includes(vinSearch.toLowerCase());
    }
    
    // Then apply general search (brand, model, title)
    if (searchQuery && !vehicle.title?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !vehicle.brand?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !vehicle.model?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Advanced filters
    if (filters.status && vehicle.status !== filters.status) return false;
    if (filters.brand && !vehicle.brand?.toLowerCase().includes(filters.brand.toLowerCase())) return false;
    if (filters.priceMin && vehicle.price < parseFloat(filters.priceMin)) return false;
    if (filters.priceMax && vehicle.price > parseFloat(filters.priceMax)) return false;
    if (filters.yearMin && vehicle.year < parseInt(filters.yearMin)) return false;
    if (filters.yearMax && vehicle.year > parseInt(filters.yearMax)) return false;
    // Duplicate VIN filter - only show vehicles with duplicate VINs
    if (filters.duplicateVin && !duplicateVins.has(vehicle.vin)) return false;
    return true;
  }).sort((a, b) => {
    // When duplicate VIN filter is active, sort by VIN so same VINs are grouped together
    if (filters.duplicateVin) {
      const vinCompare = (a.vin || '').localeCompare(b.vin || '');
      if (vinCompare !== 0) return vinCompare;
      // Secondary sort by year for same VINs
      return (b.year || 0) - (a.year || 0);
    }
    if (activeTab === 'sold') {
      const aDate = a.sold_at || '';
      const bDate = b.sold_at || '';
      return bDate.localeCompare(aDate);
    }
    return 0;
  });


  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch ALL vehicles (including sold) for admin - no status filter
      const response = await axios.get(`${API}/vehicles/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      // Fallback to regular endpoint
      try {
        const response = await axios.get(`${API}/vehicles?limit=1000`);
        setVehicles(response.data);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reactivate sold vehicle
  const handleReactivateVehicle = async (vehicleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/vehicles/${vehicleId}/status`, 
        { status: 'available' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(t('admin.vehicleReactivated'));
      fetchVehicles();
    } catch (error) {
      console.error('Error reactivating vehicle:', error);
      toast.error(t('admin.operationFailed'));
    }
  };

  // Mark vehicle as sold
  const handleMarkAsSold = async (vehicleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/vehicles/${vehicleId}/status`, 
        { status: 'sold' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(t('admin.vehicleMarkedSold'));
      fetchVehicles();
    } catch (error) {
      console.error('Error marking as sold:', error);
      toast.error(t('admin.operationFailed'));
    }
  };

  // ======= Photo Background Enhancement =======
  const handleEnhanceVehiclePhotos = async (vehicleId) => {
    if (enhancingVehicleId) return;
    setEnhancingVehicleId(vehicleId);
    try {
      const token = localStorage.getItem('token');
      toast.info(t('admin.enhancingPhotos') || 'Fotoğraflar işleniyor...');
      const res = await axios.post(
        `${API}/admin/vehicles/${vehicleId}/enhance-photos`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      if (res.data.success) {
        toast.success(`${res.data.enhanced} / ${res.data.total} ${t('admin.photosEnhanced') || 'fotoğraf işlendi'}`);
        fetchVehicles();
      } else {
        toast.error(res.data.error || t('admin.operationFailed'));
      }
    } catch (error) {
      console.error('Enhance photos error:', error);
      toast.error(t('admin.operationFailed'));
    } finally {
      setEnhancingVehicleId(null);
    }
  };

  const handleRestoreVehiclePhotos = async (vehicleId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/vehicles/${vehicleId}/restore-photos`,
        null,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (res.data.success) {
        toast.success(t('admin.originalsRestored') || 'Orijinaller geri yüklendi');
        fetchVehicles();
      } else {
        toast.error(res.data.error || t('admin.operationFailed'));
      }
    } catch (error) {
      toast.error(t('admin.operationFailed'));
    }
  };

  const handleEnhanceAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/vehicles/enhance-all-photos?only_unenhanced=true`,
        null,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (res.data.success) {
        toast.success(t('admin.batchEnhanceStarted') || 'Toplu işlem başlatıldı');
        pollEnhanceStatus();
      } else {
        toast.info(res.data.message || 'Already running');
      }
    } catch (error) {
      toast.error(t('admin.operationFailed'));
    }
  };

  const pollEnhanceStatus = () => {
    const token = localStorage.getItem('token');
    const fetchStatus = async () => {
      try {
        const res = await axios.get(
          `${API}/admin/vehicles/enhance-status`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setEnhanceStatus(res.data);
        if (res.data.status === 'running') {
          setTimeout(fetchStatus, 5000);
        } else if (res.data.status === 'finished') {
          fetchVehicles();
        }
      } catch {}
    };
    fetchStatus();
  };

  // Poll status on mount to resume view if batch already running
  useEffect(() => {
    pollEnhanceStatus();
  }, []);

  const handleEbaySync = async () => {
    setSyncing(true);
    setSyncResult(null);
    
    let pollInterval = null;
    
    const cleanup = () => {
      if (pollInterval) clearInterval(pollInterval);
      setSyncing(false);
    };
    
    try {
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error(t('admin.pleaseLogin'), { duration: 5000 });
        cleanup();
        return;
      }
      
      // Start sync in background - get job_id
      const response = await axios.post(`${API}/ebay/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data || !response.data.success) {
        cleanup();
        toast.error(response.data?.message || 'eBay sync failed', {
          duration: 6000,
        });
        return;
      }
      
      // Successfully started - now show info message
      toast.info('🚀 eBay sync started!\n\n✅ Running in background\n⏱️ First sync: 15-17 min\n⏱️ Next syncs: ~1 min\n\nYou can close this page, sync will continue!', {
        duration: 10000,
      });
      
      // Get job_id from response
      const jobId = response.data.job_id;
      
      if (!jobId) {
        cleanup();
        toast.error('Job ID not received', { duration: 6000 });
        return;
      }
      
      // Poll for status every 5 seconds using job_id
      let checkCount = 0;
      const maxChecks = 240; // 20 minutes max (240 * 5 seconds = 1200 seconds)
      
      pollInterval = setInterval(async () => {
        checkCount++;
        
        try {
          const statusResponse = await axios.get(`${API}/ebay/sync/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const status = statusResponse.data;
          
          // Check job status
          if (status.status === 'completed') {
            cleanup();
            
            // Successfully completed
            if (status.result && status.result.success) {
              const result = status.result;
              const newAdded = result.new_added || result.added || 0;
              const markedSold = result.marked_sold || result.sold || 0;
              const existingUnchanged = result.existing_unchanged || 0;
              const totalEbay = result.total_ebay_listings || result.total || 0;
              
              const message = `✅ ${t('admin.syncCompleted')}\n\n📥 ${newAdded} ${t('admin.syncNewAdded')}\n🔴 ${markedSold} ${t('admin.syncMarkedSold')}\n✅ ${existingUnchanged} ${t('admin.syncUnchanged')}\n📊 ${t('admin.syncTotalEbay')}: ${totalEbay}`;
              
              toast.success(message, {
                duration: 8000,
              });
              
              setSyncResult(result);
              fetchVehicles();
            } else {
              toast.warning(`⚠️ ${t('admin.syncCompletedUnclear')}`, {
                duration: 5000,
              });
            }
          } else if (status.status === 'failed') {
            cleanup();
            toast.error(`❌ ${t('admin.syncError')}: ${status.error || t('admin.unknownError')}`, {
              duration: 6000,
            });
          } else if (status.status === 'processing') {
            // Still running - update progress every 15 seconds (more frequent)
            if (checkCount % 3 === 0) { // Every 15 seconds
              const elapsed = Math.floor(checkCount * 5 / 60);
              const progressText = status.progress || 'eBay sync devam ediyor...';
              toast.info(`⏳ ${progressText}${elapsed > 0 ? ` (${elapsed} dakika)` : ''}`, {
                duration: 4000,
              });
            }
          }
          
          // Max check limit (20 minutes)
          if (checkCount >= maxChecks) {
            cleanup();
            toast.warning(`⏱️ ${t('admin.syncTimeoutMessage')}`, {
              duration: 10000,
            });
          }
          
        } catch (err) {
          console.error('Status check error:', err);
          // Don't stop on status check errors, keep polling
        }
      }, 5000); // Check every 5 seconds
      
    } catch (error) {
      console.error('eBay sync error:', error);
      cleanup();
      
      // More specific error message
      let errorMsg = `❌ ${t('admin.syncFailed')}`;
      if (error.response) {
        errorMsg += ` (${error.response.status}: ${error.response.data?.message || error.message})`;
      } else if (error.request) {
        errorMsg += ' Server unreachable.';
      } else {
        errorMsg += ` ${error.message}`;
      }
      
      toast.error(errorMsg, {
        duration: 8000,
      });
    }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingImage(true);
    const uploadedUrls = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append('file', files[i]);

        const response = await axios.post(`${API}/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const fullUrl = `${BACKEND_URL}${response.data.url}`;
        uploadedUrls.push(fullUrl);
      }

      setUploadedImages([...uploadedImages, ...uploadedUrls]);
      
      // Add to form images
      const currentImages = vehicleForm.images ? vehicleForm.images.split(',').map(i => i.trim()).filter(i => i) : [];
      const allImages = [...currentImages, ...uploadedUrls];
      setVehicleForm({...vehicleForm, images: allImages.join(', ')});
      
      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCSVImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/vehicles/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setImportResult(response.data);
      toast.success(`Successfully imported ${response.data.imported_count} vehicles!`);
      
      fetchVehicles();
      
      setTimeout(() => {
        setShowImportModal(false);
        setImportFile(null);
        setImportResult(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
      setImportResult({
        success: false,
        message: error.response?.data?.detail || 'Import failed'
      });
    } finally {
      setImporting(false);
    }
  };

 

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...vehicleForm,
        year: Number(vehicleForm.year),
        price: Number(vehicleForm.price),
        mileage: Number(vehicleForm.mileage),
        features: vehicleForm.features ? vehicleForm.features.split(',').map(f => f.trim()) : [],
        images: vehicleForm.images ? vehicleForm.images.split(',').map(i => i.trim()) : []
      };
      await axios.post(`${API}/vehicles`, data);
      toast.success('Vehicle added successfully');
      setShowAddDialog(false);
      setUploadedImages([]);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Error adding vehicle:', error);
      toast.error('Failed to add vehicle');
    }
  };

  const handleEditVehicle = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...vehicleForm,
        year: Number(vehicleForm.year),
        price: Number(vehicleForm.price),
        mileage: Number(vehicleForm.mileage),
        features: vehicleForm.features ? vehicleForm.features.split(',').map(f => f.trim()) : [],
        images: vehicleForm.images ? vehicleForm.images.split(',').map(i => i.trim()) : []
      };
      await axios.put(`${API}/vehicles/${editingVehicle.id}`, data);
      toast.success('Vehicle updated successfully');
      setEditingVehicle(null);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast.error('Failed to update vehicle');
    }
  };

  const handleDeleteVehicle = async () => {
    try {
      await axios.delete(`${API}/vehicles/${deleteVehicle.id}`);
      toast.success('Vehicle deleted successfully');
      setDeleteVehicle(null);
      fetchVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Failed to delete vehicle');
    }
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      price: vehicle.price,
      mileage: vehicle.mileage,
      color: vehicle.color,
      transmission: vehicle.transmission,
      fuel_type: vehicle.fuel_type,
      engine: vehicle.engine,
      vin: vehicle.vin || '',
      description: vehicle.description,
      features: vehicle.features?.join(', ') || '',
      images: vehicle.images?.join(', ') || '',
      is_featured: vehicle.is_featured
    });
  };

  const resetForm = () => {
    setVehicleForm({
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      price: '',
      mileage: '',
      color: '',
      transmission: 'Automatic',
      fuel_type: 'Gasoline',
      engine: '',
      vin: '',
      description: '',
      features: '',
      images: '',
      is_featured: false
    });
    setUploadedImages([]);
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="spinner"></div></div>;
  }

  // Calculate counts
  const availableCount = vehicles.filter(v => v.status === 'available').length;
  const soldCount = vehicles.filter(v => v.status === 'sold').length;
  const allCount = vehicles.length;

  return (
    <div>
      <div className="mb-6">
        {/* Status Tabs */}
        <div className="flex space-x-1 mb-4 p-1 bg-gray-100 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('available')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'available' 
                ? 'bg-white text-green-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('admin.available')} ({availableCount})
          </button>
          <button
            onClick={() => setActiveTab('sold')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'sold' 
                ? 'bg-white text-red-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('admin.sold')} ({soldCount})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'all' 
                ? 'bg-white text-blue-700 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('admin.all')} ({allCount})
          </button>
        </div>

        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('admin.manageVehicles')}</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {filteredVehicles.length} / {vehicles.length} {activeTab === 'sold' ? t('admin.soldVehicles') : t('admin.totalActiveVehicles')}
            </p>
          </div>
          <div className="flex space-x-2">
          <Button 
            onClick={handleExportExcel}
            disabled={exporting}
            variant="outline"
            className="border-gray-300"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? t('admin.downloading') : 'Excel'}
          </Button>
          <Button 
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? "default" : "outline"}
            className={showFilters ? "bg-blue-600 hover:bg-blue-700 text-white" : "border-gray-300"}
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('admin.filter')}
          </Button>
          <Button 
            onClick={handleEbaySync}
            disabled={syncing}
            className="bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400"
          >
            {syncing ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('admin.syncing')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('admin.syncFromEbay')}
              </>
            )}
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)} 
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Import CSV
          </Button>
          <Button
            onClick={handleEnhanceAll}
            disabled={enhanceStatus?.status === 'running'}
            className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-60"
            data-testid="enhance-all-photos-btn"
            title={t('admin.enhanceAllTooltip') || 'Tüm araç fotoğraflarının arkaplanını showroom ile değiştir'}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {enhanceStatus?.status === 'running'
              ? `${enhanceStatus.processed || 0}/${enhanceStatus.total_vehicles || '?'}`
              : (t('admin.enhanceAllPhotos') || 'Fotoğrafları AI ile Showroom\'a Taşı')}
          </Button>
          <Button onClick={() => setShowForm(true)} className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="add-vehicle-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.addVehicleBtn')}
          </Button>
        </div>
        </div>

        {/* eBay Sync Result Card */}
        {enhanceStatus?.status === 'running' && (
          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-4 rounded-md" data-testid="enhance-progress-banner">
            <div className="flex items-center gap-3">
              <div className="animate-spin w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full" />
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-900">
                  {t('admin.batchEnhanceProgress') || 'Photo enhancement in progress'}
                </p>
                <p className="text-xs text-purple-700 mt-1">
                  {enhanceStatus.processed || 0} / {enhanceStatus.total_vehicles || '?'} {t('admin.vehiclesProcessed') || 'vehicles processed'}
                  {' · '}
                  {enhanceStatus.enhanced_images || 0} {t('admin.photosEnhanced') || 'photos enhanced'}
                </p>
              </div>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-2 mt-3">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all"
                style={{
                  width: `${Math.min(100, Math.round(((enhanceStatus.processed || 0) / Math.max(1, enhanceStatus.total_vehicles || 1)) * 100))}%`,
                }}
              />
            </div>
          </div>
        )}
        {syncResult && syncResult.success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-green-800">{t('admin.syncCompleted')}</h3>
                <div className="mt-2 text-sm text-green-700 space-y-1">
                  <p>📥 <strong>{syncResult.new_added || syncResult.added || 0}</strong> {t('admin.syncNewAdded')}</p>
                  <p>🔴 <strong>{syncResult.marked_sold || syncResult.sold || 0}</strong> {t('admin.syncMarkedSold')}</p>
                  {(syncResult.vin_conflicts > 0) && (
                    <p>⚠️ <strong>{syncResult.vin_conflicts}</strong> {t('admin.syncVinConflicts') || 'VIN conflicts (review needed)'}</p>
                  )}
                  <p>📊 {t('admin.syncTotalEbay')}: <strong>{syncResult.total_api_items || syncResult.total || 0}</strong></p>
                </div>
              </div>
              <button
                onClick={() => setSyncResult(null)}
                className="ml-3 text-green-500 hover:text-green-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Search Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          {/* General Search */}
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder={t('admin.searchVehicles')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setVinSearch(''); }}
              className="pl-10 pr-4 py-2 w-full"
              data-testid="admin-search-input"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="clear-search-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* VIN Search */}
          <div className="relative md:w-64">
            <Input
              type="text"
              placeholder={t('admin.searchByVin')}
              value={vinSearch}
              onChange={(e) => { setVinSearch(e.target.value); setSearchQuery(''); }}
              className="pl-10 pr-4 py-2 w-full font-mono text-sm"
              data-testid="admin-vin-search-input"
              maxLength={17}
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xs font-bold text-gray-400">VIN</span>
            {vinSearch && (
              <button
                onClick={() => setVinSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="clear-vin-search-btn"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* VIN Search Active Indicator */}
        {vinSearch && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-blue-600 font-medium">🔍 VIN Search Active:</span>
            <span className="font-mono bg-white px-2 py-1 rounded border">{vinSearch.toUpperCase()}</span>
            <span className="text-blue-600">({filteredVehicles.length} {t('admin.resultsFound')})</span>
            <Button variant="ghost" size="sm" onClick={() => setVinSearch('')} className="ml-auto">
              <X className="w-4 h-4 mr-1" /> Temizle
            </Button>
          </div>
        )}

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="p-4 rounded-lg border mt-4" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{t('admin.advancedFilters')}</h3>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> {t('admin.clear')}
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('admin.status')}</label>
                <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({...filters, status: value === 'all' ? '' : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.all')}</SelectItem>
                    <SelectItem value="available">{t('admin.available')}</SelectItem>
                    <SelectItem value="sold">{t('admin.sold')}</SelectItem>
                    <SelectItem value="pending">{t('admin.pending')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('admin.brand')}</label>
                <Select value={filters.brand || 'all'} onValueChange={(value) => setFilters({...filters, brand: value === 'all' ? '' : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('admin.all')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.all')}</SelectItem>
                    {uniqueBrands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('admin.minPrice')} ($)</label>
                <Input 
                  type="number" 
                  value={filters.priceMin} 
                  onChange={(e) => setFilters({...filters, priceMin: e.target.value})}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('admin.maxPrice')} ($)</label>
                <Input 
                  type="number" 
                  value={filters.priceMax} 
                  onChange={(e) => setFilters({...filters, priceMax: e.target.value})}
                  placeholder="∞"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('admin.minYear')}</label>
                <Input 
                  type="number" 
                  value={filters.yearMin} 
                  onChange={(e) => setFilters({...filters, yearMin: e.target.value})}
                  placeholder="2015"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('admin.maxYear')}</label>
                <Input 
                  type="number" 
                  value={filters.yearMax} 
                  onChange={(e) => setFilters({...filters, yearMax: e.target.value})}
                  placeholder="2025"
                />
              </div>
              <div className="col-span-2 flex items-center gap-3 mt-2">
                <Switch
                  id="duplicate-vin-filter"
                  checked={filters.duplicateVin}
                  onCheckedChange={(checked) => setFilters({...filters, duplicateVin: checked})}
                />
                <label htmlFor="duplicate-vin-filter" className="text-sm cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                  {t('admin.showDuplicateVins') || 'Show Duplicate VINs'} 
                  {duplicateVins.size > 0 && <span className="ml-1 text-orange-500 font-medium">({duplicateVins.size})</span>}
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions Toolbar */}
        {selectedVehicles.length > 0 && (
          <div className="flex items-center gap-4 p-3 rounded-lg mt-4 bg-blue-50 border border-blue-200">
            <span className="font-medium text-blue-800">
              {selectedVehicles.length} {t('admin.vehiclesSelected')}
            </span>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleBulkUpdateStatus('available')}
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                {t('admin.makeAvailable')}
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleBulkUpdateStatus('sold')}
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                {t('admin.markAsSold')}
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleBulkToggleFeatured(true)}
                className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
              >
                <Star className="w-3 h-3 mr-1" /> {t('admin.feature')}
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-3 h-3 mr-1" /> {t('admin.delete')}
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setSelectedVehicles([])}
              >
                {t('admin.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Vehicle Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>{t('admin.addVehicle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVehicle} className="space-y-4">
            <VehicleForm 
              vehicleForm={vehicleForm} 
              setVehicleForm={setVehicleForm} 
              t={t} 
              handleImageUpload={handleImageUpload}
              uploadingImage={uploadingImage}
              uploadedImages={uploadedImages}
              setUploadedImages={setUploadedImages}
            />
            <Button type="submit" className="w-full" data-testid="submit-add-vehicle">Add Vehicle</Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mt-4">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full" data-testid="vehicles-table">
            <thead style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }} className="border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button onClick={handleSelectAll} className="flex items-center justify-center hover:bg-gray-100 p-1 rounded">
                    {selectedVehicles.length === filteredVehicles.length && filteredVehicles.length > 0 ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.vehicle')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>VIN</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.price')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.year')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.mileage')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.featured')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredVehicles.map(vehicle => (
                <tr key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`} className={selectedVehicles.includes(vehicle.id) ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-4">
                    <button onClick={() => handleSelectVehicle(vehicle.id)} className="flex items-center justify-center hover:bg-gray-100 p-1 rounded">
                      {selectedVehicles.includes(vehicle.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <img
                        src={vehicle.images?.[0] || 'https://via.placeholder.com/60x40'}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        className="w-16 h-12 object-cover rounded mr-3"
                      />
                      <div>
                        <p className="font-semibold text-gray-900">{vehicle.brand} {vehicle.model}</p>
                        <p className="text-sm text-gray-600">{vehicle.transmission}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                      {vinSearch && vehicle.vin ? (
                        (() => {
                          const vin = vehicle.vin || '';
                          const searchLower = vinSearch.toLowerCase();
                          const vinLower = vin.toLowerCase();
                          const idx = vinLower.indexOf(searchLower);
                          if (idx >= 0) {
                            return (
                              <>
                                {vin.substring(0, idx)}
                                <span className="bg-yellow-300 text-yellow-900 font-bold">{vin.substring(idx, idx + vinSearch.length)}</span>
                                {vin.substring(idx + vinSearch.length)}
                              </>
                            );
                          }
                          return vin;
                        })()
                      ) : (
                        vehicle.vin || '-'
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-semibold text-gray-900">${vehicle.price?.toLocaleString()}</td>
                  <td className="px-6 py-4 text-gray-600">{vehicle.year}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {vehicle.synced_at && (new Date() - new Date(vehicle.synced_at)) < 2 * 24 * 60 * 60 * 1000 && (
                        <Badge className="bg-blue-500 text-white hover:bg-blue-600" data-testid={`new-badge-${vehicle.id}`}>
                          {t('admin.newBadge')}
                        </Badge>
                      )}
                      {vehicle.status === 'sold' ? (
                        <div>
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{t('admin.sold')}</Badge>
                          {vehicle.sold_at && (
                            <p className="text-xs text-gray-500 mt-1" data-testid={`sold-date-${vehicle.id}`}>
                              {new Date(vehicle.sold_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      ) : vehicle.status === 'pending' ? (
                        <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">{t('admin.pending')}</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{t('admin.available')}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{vehicle.mileage?.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    {vehicle.is_featured && <Badge className="bg-yellow-500"><Star className="w-3 h-3" /></Badge>}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end space-x-2">
                      {vehicle.status === 'sold' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivateVehicle(vehicle.id)}
                          className="border-green-500 text-green-600 hover:bg-green-50"
                          title={t('admin.reactivateTitle')}
                        >
                          {t('admin.activate')}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMarkAsSold(vehicle.id)}
                          className="border-red-500 text-red-600 hover:bg-red-50"
                          title={t('admin.markAsSoldTitle')}
                        >
                          {t('admin.sold')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(vehicle)}
                        data-testid={`edit-vehicle-${vehicle.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {!vehicle.bg_enhanced ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEnhanceVehiclePhotos(vehicle.id)}
                          disabled={enhancingVehicleId === vehicle.id}
                          className="border-purple-500 text-purple-600 hover:bg-purple-50 disabled:opacity-60"
                          title={t('admin.enhancePhotosTooltip')}
                          data-testid={`enhance-vehicle-row-${vehicle.id}`}
                        >
                          {enhancingVehicleId === vehicle.id ? (t('admin.processing') || '...') : (t('admin.enhance') || 'AI')}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreVehiclePhotos(vehicle.id)}
                          className="border-orange-500 text-orange-600 hover:bg-orange-50"
                          title={t('admin.restoreOriginalsTooltip')}
                          data-testid={`restore-vehicle-row-${vehicle.id}`}
                        >
                          {t('admin.restore') || 'Undo'}
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteVehicle(vehicle)}
                        data-testid={`delete-vehicle-${vehicle.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {/* Mobile Select All */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: 'var(--bg-secondary)' }}>
            <button onClick={handleSelectAll} className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              {selectedVehicles.length === filteredVehicles.length && filteredVehicles.length > 0 ? (
                <CheckSquare className="w-5 h-5 text-blue-600" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
              {t('admin.selectAll')}
            </button>
            <span className="text-xs text-gray-500">{filteredVehicles.length} {t('admin.vehicleCount')}</span>
          </div>

          <div className="divide-y">
            {filteredVehicles.map(vehicle => (
              <div key={vehicle.id} data-testid={`vehicle-row-${vehicle.id}`} className={`p-4 ${selectedVehicles.includes(vehicle.id) ? 'bg-blue-50' : ''}`}>
                <div className="flex gap-3">
                  {/* Checkbox */}
                  <button onClick={() => handleSelectVehicle(vehicle.id)} className="flex-shrink-0 mt-1">
                    {selectedVehicles.includes(vehicle.id) ? (
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {/* Image */}
                  <img
                    src={vehicle.images?.[0] || 'https://via.placeholder.com/80x60'}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="w-20 h-16 object-cover rounded flex-shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-bold text-gray-900 text-sm leading-tight">{vehicle.year} {vehicle.brand} {vehicle.model}</p>
                        <p className="text-lg font-bold text-green-700 mt-0.5">${vehicle.price?.toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {vehicle.synced_at && (new Date() - new Date(vehicle.synced_at)) < 2 * 24 * 60 * 60 * 1000 && (
                          <Badge className="bg-blue-500 text-white text-xs px-1.5 py-0.5">{t('admin.newBadge')}</Badge>
                        )}
                        {vehicle.status === 'sold' ? (
                          <div className="text-right">
                            <Badge className="bg-red-100 text-red-700 text-xs">{t('admin.sold')}</Badge>
                            {vehicle.sold_at && (
                              <p className="text-[10px] text-gray-500 mt-0.5">{new Date(vehicle.sold_at).toLocaleDateString('tr-TR')}</p>
                            )}
                          </div>
                        ) : vehicle.status === 'pending' ? (
                          <Badge className="bg-yellow-100 text-yellow-700 text-xs">{t('admin.pending')}</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 text-xs">{t('admin.available')}</Badge>
                        )}
                        {vehicle.is_featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                      </div>
                    </div>

                    {/* Details row */}
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{vehicle.mileage?.toLocaleString()} mi</span>
                      <span>{vehicle.transmission}</span>
                      {vehicle.vin && <span className="font-mono text-[11px]">{vehicle.vin}</span>}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-2">
                      {vehicle.status === 'sold' ? (
                        <Button variant="outline" size="sm" onClick={() => handleReactivateVehicle(vehicle.id)} className="h-7 text-xs border-green-500 text-green-600">
                          {t('admin.activate')}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => handleMarkAsSold(vehicle.id)} className="h-7 text-xs border-red-500 text-red-600">
                          {t('admin.sold')}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(vehicle)} className="h-7 text-xs" data-testid={`edit-vehicle-${vehicle.id}`}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      {!vehicle.bg_enhanced ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEnhanceVehiclePhotos(vehicle.id)}
                          disabled={enhancingVehicleId === vehicle.id}
                          className="h-7 text-xs border-purple-500 text-purple-600 disabled:opacity-60"
                          title={t('admin.enhancePhotosTooltip') || 'Fotoğrafları AI ile showroom arka planına taşı'}
                          data-testid={`enhance-vehicle-${vehicle.id}`}
                        >
                          {enhancingVehicleId === vehicle.id
                            ? (t('admin.processing') || '...')
                            : (t('admin.enhance') || 'AI Foto')}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreVehiclePhotos(vehicle.id)}
                          className="h-7 text-xs border-orange-500 text-orange-600"
                          title={t('admin.restoreOriginalsTooltip') || 'Orijinal fotoğraflara dön'}
                          data-testid={`restore-vehicle-${vehicle.id}`}
                        >
                          {t('admin.restore') || 'Geri Al'}
                        </Button>
                      )}
                      <Button variant="destructive" size="sm" onClick={() => setDeleteVehicle(vehicle)} className="h-7 text-xs" data-testid={`delete-vehicle-${vehicle.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingVehicle} onOpenChange={() => { setEditingVehicle(null); resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>{t('admin.editVehicle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditVehicle} className="space-y-4">
            <VehicleForm 
              vehicleForm={vehicleForm} 
              setVehicleForm={setVehicleForm} 
              t={t} 
              handleImageUpload={handleImageUpload}
              uploadingImage={uploadingImage}
              uploadedImages={uploadedImages}
              setUploadedImages={setUploadedImages}
            />
            <Button type="submit" className="w-full" data-testid="submit-edit-vehicle">Update Vehicle</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteVehicle} onOpenChange={() => setDeleteVehicle(null)}>
        <AlertDialogContent data-testid="delete-vehicle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.deleteVehicle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.confirmDelete')} {deleteVehicle?.brand} {deleteVehicle?.model}? {t('admin.cannotUndo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteVehicle} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Vehicles from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Select CSV File
              </label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                disabled={importing}
                className="cursor-pointer"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                CSV format: Brand, Model, Year, Price, Mileage, Color, Transmission, Fuel Type, Engine, Description, Features, Images
              </p>
            </div>
            
            {importResult && (
              <div className={`p-3 rounded ${importResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                <p className="text-sm">
                  {importResult.success 
                    ? `Successfully imported ${importResult.imported_count} vehicles!`
                    : importResult.message || 'Import failed'
                  }
                </p>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2 text-xs">
                    <p>Errors:</p>
                    {importResult.errors.slice(0, 5).map((err, i) => (
                      <p key={i}>- {err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex space-x-2">
              <Button 
                onClick={handleCSVImport} 
                disabled={!importFile || importing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                disabled={importing}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

// Sortable Image Item Component
const SortableImageItem = ({ id, url, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-lg border-2 overflow-hidden transition-all ${
        isDragging 
          ? 'border-blue-500 shadow-lg scale-105 z-50' 
          : 'border-gray-300 hover:border-blue-400'
      }`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 z-10 bg-black/50 rounded p-1 cursor-move opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical size={16} className="text-white" />
      </div>
      
      {/* Image Number Badge */}
      <div className="absolute top-1 right-1 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
        {index + 1}
      </div>
      
      {/* Delete Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(url);
        }}
        className="absolute bottom-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
        type="button"
      >
        <X size={14} />
      </button>
      
      {/* Image */}
      <img 
        src={url} 
        alt={`Image ${index + 1}`} 
        className="w-full h-24 object-cover pointer-events-none"
      />
    </div>
  );
};

// Vehicle Form Component
const VehicleForm = ({ vehicleForm, setVehicleForm, t, handleImageUpload, uploadingImage, uploadedImages, setUploadedImages }) => {
  // Hooks must be at the top level of the component
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const allImageUrls = vehicleForm.images 
    ? vehicleForm.images.split(',').map(i => i.trim()).filter(i => i)
    : [];
  
  const handleDragEnd = (event) => {
    const {active, over} = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = allImageUrls.indexOf(active.id);
      const newIndex = allImageUrls.indexOf(over.id);
      
      const newItems = arrayMove(allImageUrls, oldIndex, newIndex);
      setVehicleForm({...vehicleForm, images: newItems.join(', ')});
      setUploadedImages(newItems);
    }
  };
  
  const removeImage = (imageUrl) => {
    const items = allImageUrls.filter(url => url !== imageUrl);
    setVehicleForm({...vehicleForm, images: items.join(', ')});
    setUploadedImages(items);
  };

  return (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.brand')}</label>
        <Input value={vehicleForm.brand} onChange={(e) => setVehicleForm({...vehicleForm, brand: e.target.value})} required data-testid="vehicle-brand" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.model')}</label>
        <Input value={vehicleForm.model} onChange={(e) => setVehicleForm({...vehicleForm, model: e.target.value})} required data-testid="vehicle-model" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.year')}</label>
        <Input type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm({...vehicleForm, year: e.target.value})} required data-testid="vehicle-year" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.price')} ($)</label>
        <Input type="number" value={vehicleForm.price} onChange={(e) => setVehicleForm({...vehicleForm, price: e.target.value})} required data-testid="vehicle-price" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.mileage')} (miles)</label>
        <Input type="number" value={vehicleForm.mileage} onChange={(e) => setVehicleForm({...vehicleForm, mileage: e.target.value})} required data-testid="vehicle-mileage" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.color')}</label>
        <Input value={vehicleForm.color} onChange={(e) => setVehicleForm({...vehicleForm, color: e.target.value})} required data-testid="vehicle-color" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.engine')}</label>
        <Input value={vehicleForm.engine} onChange={(e) => setVehicleForm({...vehicleForm, engine: e.target.value})} data-testid="vehicle-engine" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.transmission')}</label>
        <Select value={vehicleForm.transmission} onValueChange={(value) => setVehicleForm({...vehicleForm, transmission: value})}>
          <SelectTrigger data-testid="vehicle-transmission">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Automatic">Automatic</SelectItem>
            <SelectItem value="Manual">Manual</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.fuelType')}</label>
        <Select value={vehicleForm.fuel_type} onValueChange={(value) => setVehicleForm({...vehicleForm, fuel_type: value})}>
          <SelectTrigger data-testid="vehicle-fuel-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Gasoline">Gasoline</SelectItem>
            <SelectItem value="Diesel">Diesel</SelectItem>
            <SelectItem value="Electric">Electric</SelectItem>
            <SelectItem value="Hybrid">Hybrid</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.vin')}</label>
      <Input 
        type="text" 
        value={vehicleForm.vin || ''} 
        onChange={(e) => setVehicleForm({...vehicleForm, vin: e.target.value.toUpperCase()})} 
        placeholder="17-character VIN (optional)"
        maxLength={17}
        data-testid="vehicle-vin"
        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} 
      />
      {vehicleForm.vin && vehicleForm.vin.length !== 17 && (
        <p className="text-sm text-yellow-600 mt-1">VIN must be exactly 17 characters</p>
      )}
    </div>
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.description')}</label>
      <Textarea value={vehicleForm.description} onChange={(e) => setVehicleForm({...vehicleForm, description: e.target.value})} required rows={4} data-testid="vehicle-description" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
    </div>
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('admin.features')}</label>
      <Textarea value={vehicleForm.features} onChange={(e) => setVehicleForm({...vehicleForm, features: e.target.value})} rows={2} data-testid="vehicle-features" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }} />
    </div>
    <div>
      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('admin.vehicleImages')}</label>
      
      {/* File Upload */}
      <div className="mb-3">
        <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{t('admin.uploadFromComputer')}</label>
        <Input
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          disabled={uploadingImage}
          className="cursor-pointer"
          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
        />
        {uploadingImage && (
          <p className="text-sm mt-1" style={{ color: 'var(--accent-blue)' }}>{t('admin.uploading')}</p>
        )}
      </div>

      {/* Display and reorder images with drag & drop */}
      {allImageUrls.length > 0 && (
          <div className="mb-3">
            <p className="text-sm mb-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
              {t('admin.vehicleImages')} ({allImageUrls.length})
            </p>
            <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
              {t('admin.dragToReorder')}
            </p>
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={allImageUrls}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {allImageUrls.map((url, index) => (
                    <SortableImageItem 
                      key={url} 
                      id={url} 
                      url={url} 
                      index={index} 
                      onRemove={removeImage}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
      )}

      {/* URL Input (Alternative) */}
      <div>
        <label className="block text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{t('admin.orEnterUrls')}</label>
        <Textarea 
          value={vehicleForm.images} 
          onChange={(e) => setVehicleForm({...vehicleForm, images: e.target.value})} 
          rows={2} 
          data-testid="vehicle-images"
          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
        />
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <Switch checked={vehicleForm.is_featured} onCheckedChange={(checked) => setVehicleForm({...vehicleForm, is_featured: checked})} data-testid="vehicle-featured" />
      <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{t('admin.featuredVehicle')}</label>
    </div>
  </>
  );
};

// Test Drives Management

export default VehiclesManagement;
